import {PAGES_MAX_AGE} from '#shared/constants';

type Options = {
	validity?: number;
};

export default function useThrottledFetchingWithCache<T>(options?: Options) {
	interface IRecord {
		data: T
		fetchedTimestamp: number
	}

	type IState = Record<string, IRecord>;

	const validity = options?.validity ?? PAGES_MAX_AGE * 1000;

	const state = ref<IState>({});
	const isFetching = ref(false);
	const pendingFetches = new Map<string, Promise<T>>();

	function fetchItems(hm: string, cb: () => Promise<T>): Promise<T> {
		// If a fetch for this exact key is already in progress, reuse it
		const pending = pendingFetches.get(hm);
		if (pending) {
			return pending;
		}

		// Cache hit — data are still valid
		const hypermarketData = state.value[hm];
		if (hypermarketData && Date.now() < hypermarketData.fetchedTimestamp + validity) {
			return Promise.resolve(hypermarketData.data);
		}

		// Cache miss — start a new fetch
		const promise = cb().then((data) => {
			state.value[hm] = {
				data,
				fetchedTimestamp: Date.now(),
			};
			return data;
		}).finally(() => {
			pendingFetches.delete(hm);
			isFetching.value = pendingFetches.size > 0;
		});

		pendingFetches.set(hm, promise);
		isFetching.value = true;

		return promise;
	}

	return {
		isFetching,
		state,
		fetchItems,
	};
}
